import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, delay } from 'rxjs/operators';

export interface NominatimPlace {
  place_id: number;
  licence: string;
  osm_type: string;
  osm_id: number;
  lat: string;
  lon: string;
  display_name: string;
  class: string;
  type: string;
  importance: number;
  icon?: string;
  address?: {
    house_number?: string;
    road?: string;
    suburb?: string;
    city?: string;
    county?: string;
    state?: string;
    postcode?: string;
    country?: string;
    country_code?: string;
  };
  boundingbox: string[];
}

export interface GeocodeResult {
  lat: number;
  lng: number;
  address: string;
  formattedAddress: string;
  place_id: string;
  confidence: number;
}

@Injectable({
  providedIn: 'root'
})
export class NominatimService {
  private readonly BASE_URL = 'https://nominatim.openstreetmap.org';
  private readonly USER_AGENT = 'BarClik/1.0.0 (contact@barclik.com)';
  
  // Rate limiting: 1 request per second
  private lastRequestTime = 0;
  private readonly MIN_INTERVAL = 1000; // 1 second

  constructor(private http: HttpClient) {}

  /**
   * Geocode: Convertir dirección a coordenadas
   */
  geocode(address: string, options?: {
    limit?: number;
    countrycodes?: string; // ISO 3166-1 alpha-2 codes (e.g., 'es,fr')
    bounded?: boolean;
    viewbox?: string; // left,top,right,bottom
  }): Observable<GeocodeResult[]> {
    return this.rateLimitedRequest(() => {
      let params = new HttpParams()
        .set('q', address)
        .set('format', 'json')
        .set('addressdetails', '1')
        .set('limit', (options?.limit || 5).toString())
        .set('accept-language', 'es,en');

      if (options?.countrycodes) {
        params = params.set('countrycodes', options.countrycodes);
      }

      if (options?.bounded && options?.viewbox) {
        params = params.set('bounded', '1');
        params = params.set('viewbox', options.viewbox);
      }

      return this.http.get<NominatimPlace[]>(`${this.BASE_URL}/search`, {
        params,
        headers: {
          'User-Agent': this.USER_AGENT
        }
      }).pipe(
        map(places => places.map(place => this.transformToGeocodeResult(place))),
        catchError(error => {
          console.error('Nominatim geocoding error:', error);
          return of([]);
        })
      );
    });
  }

  /**
   * Reverse Geocode: Convertir coordenadas a dirección
   */
  reverseGeocode(lat: number, lng: number, options?: {
    zoom?: number; // 0-18, higher = more detailed
    addressdetails?: boolean;
  }): Observable<GeocodeResult | null> {
    return this.rateLimitedRequest(() => {
      let params = new HttpParams()
        .set('lat', lat.toString())
        .set('lon', lng.toString())
        .set('format', 'json')
        .set('addressdetails', (options?.addressdetails !== false ? '1' : '0'))
        .set('zoom', (options?.zoom || 18).toString())
        .set('accept-language', 'es,en');

      return this.http.get<NominatimPlace>(`${this.BASE_URL}/reverse`, {
        params,
        headers: {
          'User-Agent': this.USER_AGENT
        }
      }).pipe(
        map(place => place ? this.transformToGeocodeResult(place) : null),
        catchError(error => {
          console.error('Nominatim reverse geocoding error:', error);
          return of(null);
        })
      );
    });
  }

  /**
   * Buscar lugares por categoría (limitado en Nominatim)
   */
  searchByCategory(query: string, category: string, lat?: number, lng?: number, radius?: number): Observable<GeocodeResult[]> {
    return this.rateLimitedRequest(() => {
      let searchQuery = `${query} ${category}`;
      
      let params = new HttpParams()
        .set('q', searchQuery)
        .set('format', 'json')
        .set('addressdetails', '1')
        .set('limit', '20')
        .set('accept-language', 'es,en');

      // Si tenemos coordenadas, crear un viewbox
      if (lat && lng && radius) {
        const offset = radius / 111000; // Aproximadamente 1 grado = 111km
        const viewbox = `${lng - offset},${lat + offset},${lng + offset},${lat - offset}`;
        params = params.set('viewbox', viewbox);
        params = params.set('bounded', '1');
      }

      return this.http.get<NominatimPlace[]>(`${this.BASE_URL}/search`, {
        params,
        headers: {
          'User-Agent': this.USER_AGENT
        }
      }).pipe(
        map(places => places
          .filter(place => this.isRelevantPlace(place, category))
          .map(place => this.transformToGeocodeResult(place))
        ),
        catchError(error => {
          console.error('Nominatim search error:', error);
          return of([]);
        })
      );
    });
  }

  /**
   * Buscar direcciones con autocompletado
   */
  autocomplete(query: string, options?: {
    countrycodes?: string;
    limit?: number;
  }): Observable<GeocodeResult[]> {
    if (query.length < 3) {
      return of([]);
    }

    return this.geocode(query, {
      limit: options?.limit || 8,
      countrycodes: options?.countrycodes || 'es'
    });
  }

  /**
   * Rate limiting para respetar los límites de Nominatim
   */
  private rateLimitedRequest<T>(requestFn: () => Observable<T>): Observable<T> {
    return new Observable(observer => {
      const now = Date.now();
      const timeSinceLastRequest = now - this.lastRequestTime;
      const delayNeeded = Math.max(0, this.MIN_INTERVAL - timeSinceLastRequest);

      if (delayNeeded > 0) {
        setTimeout(() => {
          this.lastRequestTime = Date.now();
          requestFn().subscribe({
            next: value => observer.next(value),
            error: error => observer.error(error),
            complete: () => observer.complete()
          });
        }, delayNeeded);
      } else {
        this.lastRequestTime = now;
        requestFn().subscribe({
          next: value => observer.next(value),
          error: error => observer.error(error),
          complete: () => observer.complete()
        });
      }
    });
  }

  /**
   * Transformar respuesta de Nominatim a formato interno
   */
  private transformToGeocodeResult(place: NominatimPlace): GeocodeResult {
    const formattedAddress = this.formatAddress(place);
    
    return {
      lat: parseFloat(place.lat),
      lng: parseFloat(place.lon),
      address: place.display_name,
      formattedAddress,
      place_id: place.place_id.toString(),
      confidence: place.importance || 0.5
    };
  }

  /**
   * Formatear dirección de manera legible
   */
  private formatAddress(place: NominatimPlace): string {
    const addr = place.address;
    if (!addr) return place.display_name;

    const parts: string[] = [];
    
    if (addr.house_number && addr.road) {
      parts.push(`${addr.road} ${addr.house_number}`);
    } else if (addr.road) {
      parts.push(addr.road);
    }
    
    if (addr.suburb) parts.push(addr.suburb);
    if (addr.city) parts.push(addr.city);
    if (addr.postcode) parts.push(addr.postcode);
    if (addr.country) parts.push(addr.country);

    return parts.length > 0 ? parts.join(', ') : place.display_name;
  }

  /**
   * Filtrar lugares relevantes por categoría
   */
  private isRelevantPlace(place: NominatimPlace, category: string): boolean {
    const categoryLower = category.toLowerCase();
    const type = place.type?.toLowerCase() || '';
    const className = place.class?.toLowerCase() || '';
    
    // Mapeo de categorías a tipos de Nominatim
    const categoryMappings: { [key: string]: string[] } = {
      'restaurant': ['restaurant', 'fast_food', 'food_court', 'cafe', 'bar', 'pub'],
      'cafe': ['cafe', 'coffee_shop'],
      'bar': ['bar', 'pub', 'nightclub'],
      'food': ['restaurant', 'fast_food', 'food_court', 'cafe', 'bar', 'pub', 'bakery'],
      'hotel': ['hotel', 'motel', 'hostel', 'guest_house'],
      'shop': ['shop', 'mall', 'marketplace']
    };

    const relevantTypes = categoryMappings[categoryLower] || [categoryLower];
    
    return relevantTypes.some(relevantType => 
      type.includes(relevantType) || 
      className.includes(relevantType) ||
      place.display_name.toLowerCase().includes(relevantType)
    );
  }

  /**
   * Calcular distancia entre dos puntos (en metros)
   */
  calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371e3; // Radio de la Tierra en metros
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lng2 - lng1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  /**
   * Verificar si el servicio está disponible
   */
  checkService(): Observable<boolean> {
    return this.http.get(`${this.BASE_URL}/status`, {
      headers: { 'User-Agent': this.USER_AGENT }
    }).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }
}